import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Phone, Navigation, FileText, CheckCircle2, Circle, Loader2, Lock, MapPin, Play, Square } from 'lucide-react';
import { useDriverTracking } from '../hooks/useDriverTracking';
import { ArrivalPrompt, findArrivedStop } from '../components/chauffeur/ArrivalPrompt';
import { NextRoutePreview } from '../components/chauffeur/NextRoutePreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PinScreen = ({ onSubmit, error, busy }) => {
  const [pin, setPin] = useState('');
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(pin); }}
        className="w-full max-w-xs text-center space-y-5"
        data-testid="driver-pin-form"
      >
        <Lock className="w-10 h-10 text-purple-400 mx-auto" />
        <h1 className="text-white text-2xl font-bold">Ophaalroute</h1>
        <p className="text-zinc-400 text-sm">Vul je pincode van 4 cijfers in</p>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          className="w-full text-center text-3xl tracking-[0.6em] bg-zinc-900 border border-zinc-700 text-white rounded-xl py-4 focus:outline-none focus:border-purple-500"
          data-testid="driver-pin-input"
        />
        {error && <p className="text-red-400 text-sm" data-testid="driver-pin-error">{error}</p>}
        <button
          type="submit"
          disabled={pin.length !== 4 || busy}
          className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white font-semibold rounded-xl py-4 text-lg transition-colors"
          data-testid="driver-pin-submit"
        >
          {busy ? 'Bezig…' : 'Openen'}
        </button>
      </form>
    </div>
  );
};

const TrackingBar = ({ tracking }) => {
  const { active, start, stop, lastSent, error } = tracking;
  if (!active) {
    return (
      <div className="mt-4">
        <button onClick={start} className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl py-3.5 font-bold" data-testid="driver-start-trip">
          <Play className="w-5 h-5" /> Start rit
        </button>
        <p className="text-[11px] text-zinc-400 mt-1.5 text-center">Bij Start rit wordt je locatie elke 30 sec met kantoor gedeeld, zolang deze pagina open is.</p>
        {error && <p className="text-xs text-red-400 mt-1 text-center" data-testid="driver-tracking-error">{error}</p>}
      </div>
    );
  }
  return (
    <div className="mt-4 flex items-center gap-3 bg-emerald-500/15 border border-emerald-500/40 rounded-xl px-3 py-2.5" data-testid="driver-tracking-active">
      <span className="relative flex h-3 w-3 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-emerald-300">Locatie wordt gedeeld</div>
        <div className="text-[11px] text-zinc-400">
          {error || (lastSent ? `Laatst verstuurd ${lastSent.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} · houd deze pagina open` : 'Locatie zoeken…')}
        </div>
      </div>
      <button onClick={stop} className="flex items-center gap-1 text-xs bg-zinc-800 text-white rounded-lg px-3 py-2 font-semibold" data-testid="driver-stop-trip">
        <Square className="w-3.5 h-3.5" /> Stop rit
      </button>
    </div>
  );
};

const MotorRow = ({ motor, onToggle, busy }) => (
  <button
    onClick={() => onToggle(motor)}
    disabled={busy}
    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-t border-zinc-100 transition-colors ${
      motor.picked_up ? 'bg-emerald-50' : 'bg-white active:bg-zinc-50'
    }`}
    data-testid={`driver-motor-${motor.order_id}`}
  >
    {motor.picked_up
      ? <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0" />
      : <Circle className="w-7 h-7 text-zinc-300 shrink-0" />}
    <div className="min-w-0 flex-1">
      <div className={`font-semibold ${motor.picked_up ? 'text-emerald-800 line-through' : 'text-zinc-900'}`}>
        {`${motor.make} ${motor.model}`.trim() || 'Motor'}
      </div>
      <div className="text-xs text-zinc-500 font-mono truncate">
        {[motor.license_plate, motor.vin].filter(Boolean).join(' · ') || '—'}
      </div>
    </div>
    <span className={`text-xs font-medium ${motor.picked_up ? 'text-emerald-700' : 'text-zinc-400'}`}>
      {motor.picked_up ? 'Opgehaald' : 'Tik om af te vinken'}
    </span>
  </button>
);

const StopCard = ({ stop, index, onToggle, busyId }) => {
  const done = stop.motors.every((m) => m.picked_up);
  const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address)}&travelmode=driving`;
  return (
    <div className={`rounded-2xl overflow-hidden border ${done ? 'border-emerald-300' : 'border-zinc-200'} bg-white shadow-sm`} data-testid={`driver-stop-${index}`}>
      <div className="p-4 flex gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white shrink-0 ${done ? 'bg-emerald-600' : 'bg-purple-600'}`}>
          {index}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-zinc-900 text-lg leading-tight">{stop.company}</div>
          <div className="text-sm text-zinc-600 flex items-start gap-1 mt-1">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-zinc-400" />
            <span>{stop.address || 'Adres onbekend, bel kantoor'}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 px-4 pb-3">
        <a
          href={stop.phone ? `tel:${stop.phone.replace(/\s/g, '')}` : undefined}
          className={`flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-sm ${stop.phone ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-400 pointer-events-none'}`}
          data-testid={`driver-call-${index}`}
        >
          <Phone className="w-4 h-4" /> {stop.phone ? 'Bellen' : 'Geen nummer'}
        </a>
        <a
          href={stop.address ? navUrl : undefined}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-sm ${stop.address ? 'bg-purple-600 text-white' : 'bg-zinc-100 text-zinc-400 pointer-events-none'}`}
          data-testid={`driver-navigate-${index}`}
        >
          <Navigation className="w-4 h-4" /> Navigeren
        </a>
      </div>
      {stop.motors.map((m) => (
        <MotorRow key={m.order_id} motor={m} onToggle={onToggle} busy={busyId === m.order_id} />
      ))}
    </div>
  );
};

export default function ChauffeurPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const pinKey = `driver_pin_${token}`;
  const [pin, setPin] = useState(localStorage.getItem(pinKey) || '');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const tracking = useDriverTracking(token, pin);

  const headers = useCallback((p) => ({ 'X-Driver-Pin': p ?? pin }), [pin]);

  const openPdf = useCallback(async (p) => {
    const res = await axios.get(`${API}/driver/${token}/pdf-link`, { headers: headers(p) });
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
  }, [token, headers]);

  const load = useCallback(async (p) => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/driver/${token}/route`, { headers: headers(p) });
      setData(res.data);
      setError('');
      localStorage.setItem(pinKey, p ?? pin);
      setPin(p ?? pin);
      if (params.get('pdf') === '1' && res.data.total_motors > 0) openPdf(p);
    } catch (e) {
      localStorage.removeItem(pinKey);
      setData(null);
      setError(e?.response?.data?.detail || 'Er ging iets mis');
    } finally {
      setBusy(false);
    }
  }, [token, pin, pinKey, headers, params, openPdf]);

  useEffect(() => { if (pin) load(pin); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (data && tracking.wasActive && !tracking.active) tracking.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [dismissed, setDismissed] = useState(new Set());
  const [arrived, setArrived] = useState(null);
  const [arrivalBusy, setArrivalBusy] = useState(false);

  useEffect(() => {
    if (!data || arrived) return;
    const s = findArrivedStop(data.stops, tracking.lastPos, dismissed);
    if (s) setArrived(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking.lastPos, data]);

  const confirmArrival = async (ids) => {
    setArrivalBusy(true);
    const done = new Set();
    for (const id of ids) {
      try {
        await axios.post(`${API}/driver/${token}/picked-up`,
          { order_id: id, picked_up: true, location: tracking.positionRef.current }, { headers: headers() });
        done.add(id);
      } catch (e) { /* blijft open, kan handmatig */ }
    }
    setData((d) => {
      const stops = d.stops.map((s) => ({ ...s, motors: s.motors.map((m) => (done.has(m.order_id) ? { ...m, picked_up: true } : m)) }));
      return { ...d, stops, picked_count: stops.flatMap((s) => s.motors).filter((m) => m.picked_up).length };
    });
    if (done.size < ids.length) alert('Niet alles kon worden opgeslagen, vink de rest handmatig af');
    setDismissed((x) => new Set(x).add(arrived.company));
    setArrived(null);
    setArrivalBusy(false);
  };

  const toggle = async (motor) => {
    setBusyId(motor.order_id);
    try {
      await axios.post(`${API}/driver/${token}/picked-up`,
        { order_id: motor.order_id, picked_up: !motor.picked_up, location: tracking.positionRef.current }, { headers: headers() });
      setData((d) => {
        const stops = d.stops.map((s) => ({ ...s, motors: s.motors.map((m) => (m.order_id === motor.order_id ? { ...m, picked_up: !m.picked_up } : m)) }));
        return { ...d, stops, picked_count: stops.flatMap((s) => s.motors).filter((m) => m.picked_up).length };
      });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Opslaan mislukt, probeer opnieuw');
    } finally {
      setBusyId(null);
    }
  };

  if (!data) {
    if (busy && pin) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-10 h-10 text-purple-400 animate-spin" /></div>;
    return <PinScreen onSubmit={(p) => load(p)} error={error} busy={busy} />;
  }

  const pct = data.total_motors ? Math.round((data.picked_count / data.total_motors) * 100) : 0;
  return (
    <div className="min-h-screen bg-zinc-100 pb-10" data-testid="driver-page">
      <div className="bg-zinc-950 text-white px-5 pt-6 pb-5 sticky top-0 z-10">
        <div className="text-xs uppercase tracking-widest text-purple-300">Ophaalroute</div>
        <div className="flex items-end justify-between">
          <h1 className="text-2xl font-bold" data-testid="driver-name">{data.driver}</h1>
          <div className="text-right text-sm" data-testid="driver-progress">
            <span className="text-2xl font-bold">{data.picked_count}</span>/{data.total_motors} opgehaald
          </div>
        </div>
        <div className="h-2 bg-zinc-800 rounded-full mt-3 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <a href={data.google_maps_url || undefined} target="_blank" rel="noopener noreferrer"
             className="flex items-center justify-center gap-2 bg-purple-600 rounded-xl py-3 text-sm font-semibold"
             data-testid="driver-full-route">
            <Navigation className="w-4 h-4" /> Hele route
          </a>
          <button onClick={() => openPdf().catch(() => alert('PDF openen mislukt'))}
                  disabled={!data.total_motors}
                  className="flex items-center justify-center gap-2 bg-white text-zinc-900 rounded-xl py-3 text-sm font-semibold disabled:opacity-40"
                  data-testid="driver-open-pdf">
            <FileText className="w-4 h-4" /> Route-PDF
          </button>
        </div>
        <TrackingBar tracking={tracking} />
        {arrived && (
          <ArrivalPrompt stop={arrived} busy={arrivalBusy} onConfirm={confirmArrival}
                         onLater={() => { setDismissed((x) => new Set(x).add(arrived.company)); setArrived(null); }} />
        )}
      </div>
      <div className="p-4 space-y-4 max-w-xl mx-auto">
        {data.stops.length === 0 && (
          <div className="text-center text-zinc-500 py-16" data-testid="driver-empty">Er staan geen motoren op je route.</div>
        )}
        {data.stops.map((s, i) => (
          <StopCard key={`${s.company}-${i}`} stop={s} index={i + 1} onToggle={toggle} busyId={busyId} />
        ))}
        <div className="text-center text-xs text-zinc-500 pt-2">Eindbestemming: Coppet, Zwitserland</div>
        {data.next_route && <NextRoutePreview route={data.next_route} />}
      </div>
    </div>
  );
}
